import * as React from 'react';
import dayjs from 'dayjs';
import { cn } from '@/lib/utils';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface TimePickerInput2Props {
    /** ISO 문자열 */
    value: string | undefined;
    onChange: (iso: string) => void;
    placeholder?: string;
    disabled?: boolean;
    compact?: boolean;
    /** 종료 시간용 — 24:00(= 다음날 00:00) 허용 */
    allowDayEnd?: boolean;
    className?: string;
}

const VALID_MINUTES = [0, 10, 20, 30, 40, 50] as const;
const REGULAR_HOURS = Array.from({ length: 24 }, (_, i) => i); // 0..23

type Parts = { h: number; m: number };

const isoToParts = (iso: string | undefined, allowDayEnd: boolean): Parts | null => {
    if (!iso) return null;
    const d = dayjs(iso);
    if (!d.isValid()) return null;
    const h = d.hour();
    const m = d.minute();
    if (allowDayEnd && h === 0 && m === 0) return { h: 24, m: 0 };
    return { h, m };
};

const partsToIso = (h: number, m: number, baseIso?: string): string => {
    const base = baseIso ? dayjs(baseIso) : dayjs();
    return base.hour(h).minute(m).second(0).millisecond(0).toISOString();
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * 세그먼트 타임 피커 — HH + MM 두 드롭다운.
 * - HH: 직접 타이핑 + 클릭 시 시간 그리드 팝오버
 * - MM: 10분 단위 6개 옵션 드롭다운
 * - HH=24 입력 시 MM은 자동 00 고정
 */
export function TimePickerInput2({
    value,
    onChange,
    disabled,
    compact,
    allowDayEnd = false,
    className,
}: TimePickerInput2Props) {
    const [hour, setHour] = React.useState<number | null>(null);
    const [minute, setMinute] = React.useState<number | null>(null);
    const [hourText, setHourText] = React.useState<string>('');
    const [hourOpen, setHourOpen] = React.useState(false);
    const [minuteOpen, setMinuteOpen] = React.useState(false);
    const [errorShake, setErrorShake] = React.useState(false);

    const hourRef = React.useRef<HTMLInputElement>(null);
    const minuteRef = React.useRef<HTMLButtonElement>(null);

    const maxHour = allowDayEnd ? 24 : 23;

    // 외부 value 동기화
    React.useEffect(() => {
        const parts = isoToParts(value, allowDayEnd);
        if (parts) {
            setHour(parts.h);
            setMinute(parts.m);
            if (document.activeElement !== hourRef.current) {
                setHourText(pad2(parts.h));
            }
        } else {
            setHour(null);
            setMinute(null);
            if (document.activeElement !== hourRef.current) {
                setHourText('');
            }
        }
    }, [value, allowDayEnd]);

    const flashError = React.useCallback(() => {
        setErrorShake(true);
        setTimeout(() => setErrorShake(false), 420);
    }, []);

    const emit = React.useCallback(
        (h: number, m: number) => {
            const safeM = h === 24 ? 0 : m;
            setHour(h);
            setMinute(safeM);
            setHourText(pad2(h));
            onChange(partsToIso(h, safeM, value));
        },
        [value, onChange],
    );

    // --- Hour handlers ---
    const handleHourFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.select();
        setHourOpen(true);
    };

    const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/[^\d]/g, '').slice(0, 2);
        setHourText(digits);
        setHourOpen(true);

        if (digits.length === 2) {
            const h = parseInt(digits, 10);
            if (h >= 0 && h <= maxHour) {
                const nextM = h === 24 ? 0 : minute ?? 0;
                emit(h, nextM);
                setHourOpen(false);
                setTimeout(() => {
                    minuteRef.current?.focus();
                    if (h !== 24) setMinuteOpen(true);
                }, 0);
            } else {
                flashError();
            }
        }
    };

    const handleHourBlur = () => {
        if (hourText === '') {
            setHourText(hour !== null ? pad2(hour) : '');
            return;
        }
        const h = parseInt(hourText, 10);
        if (Number.isNaN(h) || h < 0 || h > maxHour) {
            flashError();
            setHourText(hour !== null ? pad2(hour) : '');
            return;
        }
        const nextM = h === 24 ? 0 : minute ?? 0;
        emit(h, nextM);
    };

    const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowRight') {
            if (hourText !== '') {
                const h = parseInt(hourText, 10);
                if (h >= 0 && h <= maxHour) {
                    e.preventDefault();
                    const nextM = h === 24 ? 0 : minute ?? 0;
                    emit(h, nextM);
                    setHourOpen(false);
                    minuteRef.current?.focus();
                    if (e.key === 'Enter' && h !== 24) setMinuteOpen(true);
                    return;
                }
            }
            if (e.key !== 'Tab') {
                e.preventDefault();
                flashError();
            }
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setHourOpen(false);
            setHourText(hour !== null ? pad2(hour) : '');
            hourRef.current?.blur();
            return;
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const cur = hour ?? 9;
            const delta = e.key === 'ArrowUp' ? 1 : -1;
            let next = cur + delta;
            if (next < 0) next = maxHour;
            if (next > maxHour) next = 0;
            emit(next, next === 24 ? 0 : minute ?? 0);
        }
    };

    const handleHourPick = (h: number) => {
        const nextM = h === 24 ? 0 : minute ?? 0;
        emit(h, nextM);
        setHourOpen(false);
        setTimeout(() => {
            minuteRef.current?.focus();
            if (h !== 24) setMinuteOpen(true);
        }, 0);
    };

    // --- Minute handlers ---
    const handleMinutePick = (m: number) => {
        if (hour === 24 && m !== 0) return;
        emit(hour ?? 9, m);
        setMinuteOpen(false);
    };

    const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (hour === 24) return;
            setMinuteOpen((o) => !o);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setMinuteOpen(false);
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            hourRef.current?.focus();
            hourRef.current?.select();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (hour === 24) return;
            const curM = minute ?? 0;
            const idx = VALID_MINUTES.indexOf(curM as any);
            const delta = e.key === 'ArrowUp' ? 1 : -1;
            const nextIdx = (idx + delta + VALID_MINUTES.length) % VALID_MINUTES.length;
            emit(hour ?? 9, VALID_MINUTES[nextIdx]);
        } else if (e.key === 'Tab') {
            setMinuteOpen(false);
        }
    };

    // --- Disabled view ---
    if (disabled) {
        return (
            <div
                className={cn(
                    'inline-flex items-center justify-center rounded-[14px] bg-[#F5F6F8] border border-dashed border-[#D9DDE3]',
                    compact ? 'h-[48px] w-[132px]' : 'h-[54px] w-[152px]',
                    className,
                )}
            >
                <span className="text-[14px] text-[#9AA1AB]">휴무</span>
            </div>
        );
    }

    const sizeCls = compact ? 'h-[48px] w-[132px]' : 'h-[54px] w-[152px]';
    const segment24 = hour === 24;

    return (
        <div
            className={cn(
                'relative inline-flex items-center gap-[6px] pl-[18px] pr-[14px]',
                'bg-white rounded-[14px] border transition-all',
                'focus-within:border-[#3D73DD] focus-within:shadow-[0_0_0_3px_rgba(61,115,221,0.12)]',
                errorShake
                    ? 'border-[#D93043] shake'
                    : hourOpen || minuteOpen
                      ? 'border-[#3D73DD]'
                      : 'border-[#E1E4E8] hover:border-[#B9C0CA]',
                sizeCls,
                className,
            )}
        >
            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-3px); }
                    75% { transform: translateX(3px); }
                }
                .shake { animation: shake 0.4s ease-out; }
            `}</style>

            <Popover open={hourOpen} onOpenChange={setHourOpen} modal={false}>
                <PopoverAnchor asChild>
                    <input
                        ref={hourRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={2}
                        value={hourText}
                        placeholder="HH"
                        onChange={handleHourChange}
                        onBlur={handleHourBlur}
                        onKeyDown={handleHourKeyDown}
                        onFocus={handleHourFocus}
                        className={cn(
                            'w-[30px] text-center outline-none bg-transparent',
                            'text-[18px] font-medium tracking-wide tabular-nums text-[#1F2430]',
                            'placeholder:text-[#C3CCD6] placeholder:font-normal',
                            'rounded-[6px] caret-[#3D73DD]',
                        )}
                    />
                </PopoverAnchor>
                <PopoverContent
                    align="start"
                    sideOffset={10}
                    className="p-[10px] bg-white border border-solid border-[#E1E4E8] shadow-[0_10px_28px_rgba(15,20,30,0.1)] rounded-[16px] w-auto z-[99999]"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => {
                        // HH 인풋 자체 클릭은 "outside"로 취급하지 않음 → 팝오버 유지
                        const target = e.target as Node | null;
                        if (target && hourRef.current && hourRef.current.contains(target)) {
                            e.preventDefault();
                        }
                    }}
                >
                    <HourGrid currentH={hour} allowDayEnd={allowDayEnd} onSelect={handleHourPick} />
                </PopoverContent>
            </Popover>

            <span className="text-[18px] font-medium text-[#C3CCD6] pb-[1px] select-none">:</span>

            <Popover open={minuteOpen} onOpenChange={setMinuteOpen} modal={false}>
                <PopoverTrigger asChild>
                    <button
                        ref={minuteRef}
                        type="button"
                        onKeyDown={handleMinuteKeyDown}
                        disabled={segment24}
                        className={cn(
                            'w-[30px] text-center outline-none bg-transparent cursor-pointer',
                            'text-[18px] font-medium tracking-wide tabular-nums',
                            'rounded-[6px] transition-colors',
                            segment24
                                ? 'text-[#1F2430] cursor-not-allowed bg-[#F2F4F6]'
                                : 'text-[#1F2430] hover:bg-[#F2F4F6] focus:bg-[#EEF3FC]',
                            minuteOpen && !segment24 ? 'bg-[#EEF3FC]' : '',
                        )}
                    >
                        {minute !== null ? pad2(minute) : <span className="text-[#C3CCD6] font-normal">MM</span>}
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    sideOffset={10}
                    className="p-[10px] bg-white border border-solid border-[#E1E4E8] shadow-[0_10px_28px_rgba(15,20,30,0.1)] rounded-[16px] w-auto z-[99999]"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => {
                        // 분 버튼 자체 클릭은 Trigger가 처리. HH 인풋 클릭은 예외
                        const target = e.target as Node | null;
                        if (target && hourRef.current && hourRef.current.contains(target)) {
                            e.preventDefault();
                        }
                    }}
                >
                    <div className="flex flex-col gap-[4px]">
                        {VALID_MINUTES.map((m) => {
                            const selected = minute === m;
                            const dis = segment24 && m !== 0;
                            return (
                                <button
                                    key={m}
                                    type="button"
                                    disabled={dis}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleMinutePick(m)}
                                    className={cn(
                                        'w-[64px] h-[40px] rounded-[10px] text-[16px] font-medium tabular-nums transition-colors outline-none',
                                        selected
                                            ? 'bg-[#3D73DD] text-white'
                                            : 'text-[#1F2430] hover:bg-[#F2F4F6]',
                                        dis ? 'opacity-30 cursor-not-allowed hover:bg-transparent' : '',
                                    )}
                                >
                                    {pad2(m)}
                                </button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>

            <ChevronIcon />
        </div>
    );
}

/** 시(HH) 선택 — MM과 동일한 세로 리스트 (스크롤) + (옵션) 24:00 */
const HourGrid = ({
    currentH,
    allowDayEnd,
    onSelect,
}: {
    currentH: number | null;
    allowDayEnd: boolean;
    onSelect: (h: number) => void;
}) => {
    const listRef = React.useRef<HTMLDivElement>(null);
    const hourList = allowDayEnd ? [...REGULAR_HOURS, 24] : REGULAR_HOURS;

    // 마운트 시 현재 값으로 스크롤 정렬
    React.useEffect(() => {
        if (!listRef.current || currentH === null) return;
        const row = 44; // 40(btn) + 4(gap)
        const idx = hourList.indexOf(currentH);
        if (idx < 0) return;
        listRef.current.scrollTop = idx * row - listRef.current.clientHeight / 2 + row / 2;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={listRef}
            className="flex flex-col gap-[4px] max-h-[280px] overflow-y-auto scrollbar-hide"
        >
            {hourList.map((h) => {
                const selected = currentH === h;
                return (
                    <button
                        key={h}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onSelect(h)}
                        className={cn(
                            'w-[64px] h-[40px] shrink-0 rounded-[10px] text-[16px] font-medium tabular-nums transition-colors outline-none',
                            selected
                                ? 'bg-[#3D73DD] text-white'
                                : 'text-[#1F2430] hover:bg-[#F2F4F6]',
                        )}
                    >
                        {pad2(h)}
                    </button>
                );
            })}
        </div>
    );
};

const ChevronIcon = () => (
    <svg
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="none"
        className="ml-auto opacity-60 pointer-events-none"
    >
        <path d="M1 1L5 5L9 1" stroke="#9AA1AB" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
